import React, {createContext, memo, useCallback, useContext, useEffect, useRef, useState, useSyncExternalStore} from "react";

// A mounted provider owns live subscriptions, not persisted access decisions.
// Route consumers may unmount without restarting the source. Provider teardown
// (including an account change) tears down every source and discards its values.
export function createLiveSessionScope(useSource) {
  const Context = createContext(null);
  const Source = memo(function Source({entry, publish}) {
    const value = useSource(...entry.args);
    useEffect(() => { publish(entry, value); }, [entry, publish, value]);
    return null;
  });
  function Provider({children}) {
    const entries = useRef(new Map());
    const [, redraw] = useState(0);
    const [store] = useState(() => ({
      get: key => entries.current.get(key)?.value,
      register(key, args) {
        if (!entries.current.get(key)?.args) {
          const prior = entries.current.get(key);
          entries.current.set(key, {key, args, value: undefined, listeners: prior?.listeners || new Set()});
          redraw(n => n + 1);
        }
      },
      subscribe(key, listener) {
        if (!entries.current.has(key)) entries.current.set(key, {key, args: null, listeners: new Set()});
        const entry = entries.current.get(key);
        entry.listeners.add(listener);
        return () => entry.listeners.delete(listener);
      },
    }));
    const publish = useCallback((entry, value) => {
      entry.value = value;
      entry.listeners.forEach(listener => listener());
    }, []);
    return <Context.Provider value={store}>
      {[...entries.current.values()].filter(entry => entry.args).map(entry => <Source key={entry.key} entry={entry} publish={publish} />)}
      {children}
    </Context.Provider>;
  }
  function useShared(key, args, enabled = true) {
    const store = useContext(Context);
    // Register before subscribing. Arguments belong to the immutable resource
    // key; changes in identity must use a different key.
    const initialArgs = useRef(args);
    initialArgs.current = args;
    useEffect(() => {
      if (store && enabled) store.register(key, initialArgs.current);
    }, [store, key, enabled]);
    const subscribe = useCallback(listener => store && enabled ? store.subscribe(key, listener) : () => {}, [store, key, enabled]);
    const snapshot = useCallback(() => store && enabled ? store.get(key) : undefined, [store, key, enabled]);
    return {provided: Boolean(store), value: useSyncExternalStore(subscribe, snapshot, snapshot)};
  }
  return {Provider, useShared};
}
